import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import MarketingLayout from "@/components/MarketingLayout";
import Seo, { ORG_JSONLD, SOFTWARE_JSONLD } from "@/components/Seo";
import { PriceCalculator } from "@/components/PriceCalculator";
import { NarratedByLottie } from "@/components/NarratedByLottie";
import MarketingVideoCards from "@/components/MarketingVideoCards";
import { shouldAutoplayVideo } from "@/lib/autoplay";
import { API } from "@/lib/api";
import {
  ArrowRight, PlugsConnected, CalendarCheck, ChartLineUp, Receipt, ArrowsClockwise,
  Stack, ShieldCheck, CheckCircle,
  Play, PencilSimpleLine, Eye, Buildings, UsersThree, Certificate, MagnifyingGlass,
  ClipboardText, Gauge, LockKey, Quotes, SpeakerSimpleHigh, SpeakerSimpleX, CornersOut, X,
} from "@phosphor-icons/react";

const HERO = "/lottie-features.jpg";

const LOTTIE_WORDS = [
  ["L", "edger"], ["O", "ptimisation"], ["T", "hrough"],
  ["T", "ask"], ["I", "ntelligence"], ["E", "fficiency"],
];

const XERO_POINTS = ["Designed exclusively for Xero", "No spreadsheets", "No CSV imports", "No duplicate data entry"];

const WORKFLOW = [
  { icon: PlugsConnected, t: "Connect Xero", d: "Authorise your organisation through Xero's secure OAuth connection." },
  { icon: MagnifyingGlass, t: "Identify transactions", d: "Lottie surfaces invoices & bills on your chosen codes." },
  { icon: CalendarCheck, t: "Create schedule", d: "Set the release basis and period — in seconds." },
  { icon: ClipboardText, t: "Review journals", d: "Check every proposed journal before anything moves." },
  { icon: CheckCircle, t: "Approve", d: "Two-person approval keeps control with your team." },
  { icon: Receipt, t: "Post to Xero", d: "Journals post directly back into your ledger." },
  { icon: ChartLineUp, t: "Registers update", d: "Your live registers and forecasts update automatically." },
];

const OUTCOMES = [
  { icon: Gauge, t: "Faster month-end", d: "Close in a fraction of the time — the repetitive admin runs itself." },
  { icon: Stack, t: "Less spreadsheet maintenance", d: "Retire the fragile prepayment and accrual workbooks for good." },
  { icon: ShieldCheck, t: "Fewer forgotten journals", d: "Recurring accruals and releases never slip through a busy month." },
  { icon: ArrowsClockwise, t: "More consistent processing", d: "Every entity is handled the same way, every single period." },
  { icon: Eye, t: "Easier reviews", d: "Reviewers see exactly what changed and why, with the working behind it." },
  { icon: Certificate, t: "Better audit readiness", d: "A complete audit trail and reconciliation sits behind every figure." },
  { icon: ChartLineUp, t: "Clear future visibility", d: "See the next twelve months of releases across every category." },
];

const CONTROL = [
  { icon: Eye, t: "Review before posting", d: "Review and approve journals before they post — or let trusted schedules run automatically." },
  { icon: PencilSimpleLine, t: "Amend any schedule", d: "Adjust the basis, period or amounts whenever the facts change." },
  { icon: ShieldCheck, t: "Full audit trail", d: "Every schedule, edit and posting is logged with who and when." },
  { icon: ClipboardText, t: "You own the treatment", d: "Lottie handles the admin — the accounting judgement stays yours." },
];

const PRACTICES = [
  { icon: ArrowsClockwise, t: "Consistency across clients", d: "Standardise month-end treatment across every Xero organisation." },
  { icon: UsersThree, t: "Team collaboration", d: "Preparer and reviewer work side by side with notes and approvals." },
  { icon: Stack, t: "Less spreadsheet reliance", d: "Move the whole practice off ad-hoc workbooks and onto one register." },
  { icon: Gauge, t: "Better scalability", d: "Take on more clients without adding month-end headcount." },
];

const SECURITY = [
  { icon: PlugsConnected, t: "Secure Xero connection", d: "Official Xero OAuth — Lottie never sees or stores your Xero password, and you approve every permission at connection." },
  { icon: LockKey, t: "Encrypted in transit", d: "All data moves over encrypted HTTPS between your browser, Lottie and Xero." },
  { icon: Eye, t: "You control the journals", d: "Post automatically or hold everything behind two-person approval — nothing moves without the rules you set." },
  { icon: ClipboardText, t: "Complete audit trail", d: "Every schedule, edit, approval and posting is logged with the user and timestamp, and is fully exportable." },
  { icon: ShieldCheck, t: "Two-factor authentication", d: "Protect your account with 2FA and secure session handling throughout the app." },
  { icon: ArrowsClockwise, t: "Disconnect anytime", d: "Revoke access from Xero or disconnect in-app whenever you like — you stay in control of your data." },
];

const TESTIMONIAL = {
  quote: "Month-end used to mean days of spreadsheet wrangling across our Xero clients. Lottie builds the schedules, posts the journals and keeps every register reconciled — we close faster, with a clean audit trail behind every figure and the team freed up for the work that actually needs judgement.",
  name: "Cloud Accounting Support Services Ltd",
  role: "Xero accounting practice",
};

const TOUR = [
  { img: "/screens/entity.png", t: "Open a Xero organisation", d: "See that entity's live balances, month-end progress and recent activity at a glance." },
  { img: "/screens/schedule.png", t: "Create a schedule", d: "Choose the release basis and period; Lottie previews every monthly journal." },
  { img: "/screens/journal.png", t: "Review the journals", d: "Check each proposed journal, leave notes, and amend before anything posts." },
  { img: "/screens/register.png", t: "Post — the register updates", d: "Approved journals post straight to Xero and your register updates automatically." },
  { img: "/screens/forecast.png", t: "See the 12-month forecast", d: "Watch opening balances, monthly releases and closing positions project twelve months ahead." },
  { img: "/screens/close.png", t: "Complete month-end close", d: "Work through the close checklist, reconcile the registers and sign off — all in one place." },
];
const TOUR_BY_TITLE = Object.fromEntries(TOUR.map((s) => [s.t, s]));

function ProductTour() {
  const [active, setActive] = useState(0);
  const [tour, setTour] = useState(null);
  const [muted, setMuted] = useState(true);
  const [lightbox, setLightbox] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/tour/status`).then((r) => r.json()).then(setTour).catch(() => {});
  }, []);

  const hasVideo = !!tour?.has_video;

  // Auto-advance the slideshow only when there's no video to play.
  useEffect(() => {
    if (hasVideo) return;
    const id = setInterval(() => setActive((a) => (a + 1) % TOUR.length), 4000);
    return () => clearInterval(id);
  }, [hasVideo]);

  // Silent autoplay-on-scroll: play muted + looping while in view, pause when out of view.
  // Mobile browsers (esp. iOS Safari) only allow un-gestured playback when the element is
  // genuinely muted + inline — and React's `muted` prop isn't reliably reflected to the DOM
  // attribute iOS actually checks, so we set the real attributes imperatively here.
  useEffect(() => {
    if (!hasVideo) return;
    const v = videoRef.current;
    if (!v) return;
    // Respect Data Saver / slow connections — don't autoplay, show the poster instead.
    if (!shouldAutoplayVideo()) {
      v.autoplay = false;
      v.removeAttribute("autoplay");
      v.pause?.();
      return;
    }
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute("muted", "");
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");
    const tryPlay = () => {
      const p = v.play();
      if (p && p.catch) p.catch(() => { v.muted = true; v.play?.().catch(() => {}); });
    };
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) tryPlay();
      else v.pause?.();
    }, { threshold: 0.25 });
    io.observe(v);
    return () => io.disconnect();
  }, [hasVideo]);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    setMuted(next);
    if (!next) v.play?.().catch(() => {});
  };

  // Lightbox: pause the inline video while the large player is open; close on Esc.
  useEffect(() => {
    if (!lightbox) return;
    videoRef.current?.pause?.();
    const onKey = (e) => { if (e.key === "Escape") setLightbox(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [lightbox]);

  // Let the hero "Watch Lottie in Action" button open the full tour: scroll here, then
  // open the lightbox (or just reveal the section if the video isn't ready yet).
  useEffect(() => {
    const open = () => {
      document.getElementById("tour")?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (hasVideo) setTimeout(() => setLightbox(true), 350);
    };
    window.addEventListener("open-tour", open);
    return () => window.removeEventListener("open-tour", open);
  }, [hasVideo]);

  const seekTo = (i) => {
    setActive(i);
    const ch = tour?.chapters?.[i];
    const v = videoRef.current;
    if (v && ch) { v.currentTime = ch.start; v.play?.().catch(() => {}); }
  };

  const onTime = () => {
    const v = videoRef.current;
    if (!v || !tour?.chapters?.length) return;
    let bestIdx = 0, bestStart = -1;
    tour.chapters.forEach((c, i) => {
      if (v.currentTime >= c.start - 0.05 && c.start >= bestStart) { bestStart = c.start; bestIdx = i; }
    });
    setActive(bestIdx);
  };

  // When the video is ready, drive the step list from the (editable, reorderable)
  // backend chapters; otherwise fall back to the static slideshow steps.
  const navItems = (hasVideo && tour?.chapters?.length)
    ? tour.chapters.map((c) => ({ t: c.title, d: c.desc || TOUR_BY_TITLE[c.title]?.d || "", img: TOUR_BY_TITLE[c.title]?.img }))
    : TOUR;

  return (
    <>
    <div className="grid lg:grid-cols-[380px_1fr] gap-8 items-start" data-testid="product-tour">
      <div className="space-y-2">
        {navItems.map((s, i) => (
          <button key={i} onClick={() => (hasVideo ? seekTo(i) : setActive(i))} data-testid={`tour-step-${i}`}
            className={`w-full text-left rounded-2xl p-5 transition-all border ${active === i ? "bg-white border-slate-200 shadow-card" : "bg-transparent border-transparent hover:bg-white/60"}`}>
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full flex items-center justify-center font-heading font-bold text-sm shrink-0"
                style={{ background: active === i ? "#F37A24" : "rgba(14,15,63,0.08)", color: active === i ? "#fff" : "#0E0F3F" }}>{i + 1}</span>
              <div className={`font-heading font-bold ${active === i ? "text-navy" : "text-navy/70"}`}>{s.t}</div>
            </div>
            {active === i && <p className="text-navy/70 text-sm mt-2 pl-11 leading-relaxed">{s.d}</p>}
          </button>
        ))}
      </div>
      <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-card bg-white">
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-slate-100 bg-slate-50">
          <span className="w-3 h-3 rounded-full bg-slate-300" /><span className="w-3 h-3 rounded-full bg-slate-300" /><span className="w-3 h-3 rounded-full bg-slate-300" />
          <span className="ml-3 text-xs text-navy/65 font-medium">app.workwithlottie.com</span>
          {hasVideo && (
            <button onClick={() => setLightbox(true)} data-testid="tour-expand"
              className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-orangeink hover:opacity-80 transition-opacity">
              <CornersOut size={14} weight="bold" /> Watch full tour
            </button>
          )}
        </div>
        {hasVideo ? (
          <div className="relative">
            <video ref={videoRef} data-testid="tour-video" controls playsInline muted autoPlay loop preload="metadata"
              poster={`${API}/tour/thumbnail`} onTimeUpdate={onTime} className="w-full block bg-navy">
              <source src={`${API}/tour/video`} type="video/mp4" />
              <track kind="captions" src={`${API}/tour/captions`} srcLang="en" label="English" default />
            </video>
            <button onClick={toggleMute} data-testid="tour-mute-toggle"
              aria-label={muted ? "Unmute the tour" : "Mute the tour"}
              className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-white text-xs font-semibold pl-2.5 pr-3 py-1.5 rounded-full shadow-lg backdrop-blur-sm hover:opacity-90 transition-opacity"
              style={{ background: muted ? "#C2410C" : "rgba(14,15,63,0.72)" }}>
              {muted ? <><SpeakerSimpleX size={15} weight="fill" /> Tap for sound</>
                     : <><SpeakerSimpleHigh size={15} weight="fill" /> Mute</>}
            </button>
          </div>
        ) : (
          TOUR.map((s, i) => (
            <img key={i} src={s.img} alt={s.t} loading="lazy"
              className={`w-full block transition-opacity duration-500 ${active === i ? "opacity-100" : "opacity-0 absolute inset-0 top-[45px]"}`} />
          ))
        )}
      </div>
    </div>
    {lightbox && hasVideo && (
      <div onClick={() => setLightbox(false)} data-testid="tour-lightbox"
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
        style={{ background: "rgba(6,7,30,0.88)", backdropFilter: "blur(6px)" }}>
        <button onClick={() => setLightbox(false)} data-testid="tour-lightbox-close" aria-label="Close"
          className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-colors">
          <X size={24} weight="bold" />
        </button>
        <div onClick={(e) => e.stopPropagation()} className="w-full max-w-5xl">
          <video data-testid="tour-lightbox-video" controls autoPlay playsInline
            className="w-full rounded-2xl shadow-2xl bg-navy">
            <source src={`${API}/tour/video`} type="video/mp4" />
            <track kind="captions" src={`${API}/tour/captions`} srcLang="en" label="English" default />
          </video>
        </div>
      </div>
    )}
    </>
  );
}

export default function MarketingHome() {
  return (
    <MarketingLayout>
      <Seo path="/" jsonLd={[ORG_JSONLD, SOFTWARE_JSONLD]} />      {/* Hero */}
      <section className="relative overflow-hidden bg-navy min-h-[620px] lg:min-h-[86vh] flex items-center" data-testid="hero-section">
        <img src={HERO} alt="Lottie — your intelligent month-end assistant" data-testid="hero-image"
          fetchpriority="high" decoding="async"
          className="absolute inset-y-0 right-0 h-full w-auto max-w-none object-cover"
          style={{ objectPosition: "center 22%", WebkitMaskImage: "linear-gradient(to right, transparent 0%, #000 42%)", maskImage: "linear-gradient(to right, transparent 0%, #000 42%)" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/70 to-navy/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/75 via-transparent to-navy/15" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(1000px 500px at 14% 16%, rgba(242,122,36,0.14), transparent 60%)" }} />
        <div className="relative w-full max-w-6xl mx-auto px-6 py-20">
          <div className="max-w-xl fade-up">
            <div className="overline mb-4" style={{ color: "#F37A24" }}>Your intelligent Xero month-end assistant</div>
            <h1 className="font-heading font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tighter">
              Accruals, prepayments and deferred income.<br />
              Automated.<br />
              Finally.
            </h1>
            <p className="text-white/75 text-base sm:text-lg mt-6 max-w-lg leading-relaxed">
              Lottie connects directly to Xero and automatically manages your accruals, prepayments and
              deferred income. From identifying transactions to posting journals and maintaining your
              registers, month-end becomes faster, more accurate and far less manual.
            </p>
            <p className="text-white/90 text-sm sm:text-base mt-5 flex items-start gap-2 max-w-lg">
              <Certificate size={20} weight="fill" style={{ color: "#F37A24" }} className="shrink-0 mt-0.5" />
              Built by accountants, from real month-end experience inside a modern Xero practice.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/start" data-testid="hero-start-trial"
                className="text-white font-semibold px-6 py-3.5 rounded-xl transition-colors flex items-center gap-2"
                style={{ background: "#F37A24" }}>
                Start Free Trial <ArrowRight size={18} weight="bold" />
              </Link>
              <a href="#tour" data-testid="hero-watch-demo"
                onClick={(e) => { e.preventDefault(); document.getElementById("tour")?.scrollIntoView({ behavior: "smooth" }); }}
                className="bg-white text-navy font-semibold px-6 py-3.5 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors flex items-center gap-2">
                <Play size={16} weight="fill" style={{ color: "#F37A24" }} /> Watch Lottie in Action
              </a>
            </div>
            <div className="flex items-center gap-2 mt-5 text-sm text-white/65">
              <CheckCircle size={17} weight="fill" className="text-positive" />
              No card required · Cancel anytime
            </div>
          </div>
        </div>
        {/* LOTTIE acronym — overlaid across the ball, lower-right of the hero image */}
        <div className="absolute z-10 bottom-[15%] right-0 w-full lg:w-[55%] px-6 flex justify-center pointer-events-none" data-testid="hero-lottie-acronym">
          <p className="font-heading tracking-tight text-sm sm:text-base text-center rounded-full px-5 py-2.5 shadow-lg"
            style={{ background: "rgba(14,15,63,0.72)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", border: "1px solid rgba(255,255,255,0.12)" }}>
            {LOTTIE_WORDS.map(([cap, rest], i) => (
              <span key={i}>
                <span className="font-black" style={{ color: "#F37A24" }}>{cap}</span>
                <span className="font-bold text-white">{rest}</span>
                {i < LOTTIE_WORDS.length - 1 && (
                  <span className="text-white">{i === LOTTIE_WORDS.length - 2 ? " \u0026 " : "\u00a0\u00a0"}</span>
                )}
              </span>
            ))}
          </p>
        </div>
      </section>

      {/* Xero positioning strip */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-navy/70 text-sm font-medium" data-testid="xero-strip">
          {XERO_POINTS.map((p, i) => (
            <span key={i} className="flex items-center gap-2">
              <CheckCircle size={18} weight="fill" style={{ color: "#F37A24" }} /> {p}
            </span>
          ))}
        </div>
      </section>

      {/* Instant-engagement clip row — first clip gently auto-previews, hover to preview, tap for sound */}
      <MarketingVideoCards autoPreviewFirst />

      {/* Month-end workflow (§3) */}
      <section className="max-w-6xl mx-auto px-6 py-20" data-testid="workflow-section">
        <div className="max-w-2xl">
          <div className="overline mb-3" style={{ color: "#F37A24" }}>Where Lottie fits</div>
          <h2 className="font-heading font-bold text-navy text-3xl sm:text-4xl tracking-tight">
            It slots straight into your month-end
          </h2>
          <p className="text-navy/70 mt-4">The same steps you already follow in Xero — just without the manual work in between.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {WORKFLOW.map((s, i) => (
            <div key={s.t} data-testid={`workflow-step-${i}`}
              className="relative rounded-2xl border border-slate-200 bg-white p-5 shadow-card">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(243,122,36,0.12)" }}>
                  <s.icon size={20} weight="duotone" style={{ color: "#F37A24" }} />
                </span>
                <span className="font-heading font-black text-lg text-navy/20">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="font-heading font-bold text-navy text-base mb-1">{s.t}</h3>
              <p className="text-navy/70 text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Product tour (§7 + §8) */}
      <section id="tour" className="bg-appbg border-y border-slate-200 scroll-mt-20" data-testid="tour-section">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="max-w-2xl mb-12">
            <div className="overline mb-3" style={{ color: "#F37A24" }}>See it in action</div>
            <h2 className="font-heading font-bold text-navy text-3xl sm:text-4xl tracking-tight">
              See Lottie complete a month-end process
            </h2>
            <p className="text-navy/70 mt-4">Follow a real prepayment from a Xero transaction to a posted journal and an updated register.</p>
            <div className="mt-4"><NarratedByLottie /></div>
          </div>
          <ProductTour />
          <div className="mt-10">
            <Link to="/start" data-testid="tour-cta"
              className="inline-flex items-center gap-2 text-white font-semibold px-6 py-3.5 rounded-xl transition-transform hover:scale-[1.02]" style={{ background: "#F37A24" }}>
              Start Free Trial <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* Outcomes (§5) */}
      <section className="max-w-6xl mx-auto px-6 py-20" data-testid="outcomes-section">
        <div className="max-w-2xl">
          <div className="overline mb-3" style={{ color: "#F37A24" }}>What changes at month-end</div>
          <h2 className="font-heading font-bold text-navy text-3xl sm:text-4xl tracking-tight">
            Less manual work. More time for the judgement calls.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {OUTCOMES.map((f) => (
            <div key={f.t} data-testid={`outcome-card-${f.t}`} className="card-interactive p-6">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(242,111,33,0.1)" }}>
                <f.icon size={22} weight="duotone" style={{ color: "#F37A24" }} />
              </div>
              <h3 className="font-heading font-bold text-navy text-lg mb-2">{f.t}</h3>
              <p className="text-navy/70 text-sm leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/features" className="text-navy font-semibold text-sm flex items-center gap-1.5 hover:gap-2.5 transition-all w-fit" data-testid="home-all-features">
            Explore all features <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      {/* Professional control (§4) */}
      <section className="bg-navy text-white" data-testid="control-section">
        <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline mb-3" style={{ color: "#F37A24" }}>You stay in control</div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight mb-5">
              Automation for the admin. Judgement stays with you.
            </h2>
            <p className="text-white/60 mb-8 max-w-lg leading-relaxed">
              Lottie removes the repetitive work — never the professional oversight. You review, amend and approve
              everything, and the accounting treatment is always yours.
            </p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
              {CONTROL.map((c) => (
                <div key={c.t} className="flex gap-3" data-testid={`control-${c.t}`}>
                  <c.icon size={24} weight="duotone" style={{ color: "#F37A24" }} className="shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">{c.t}</div>
                    <div className="text-white/70 text-sm leading-relaxed">{c.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-white">
            <div className="flex items-center gap-1.5 px-4 py-3 border-b border-slate-100 bg-slate-50">
              <span className="w-3 h-3 rounded-full bg-slate-300" /><span className="w-3 h-3 rounded-full bg-slate-300" /><span className="w-3 h-3 rounded-full bg-slate-300" />
            </div>
            <img src="/screens/schedule.png" alt="Reviewing and approving an adjustment before it posts to Xero" loading="lazy" className="w-full block" />
          </div>
        </div>
      </section>

      {/* Built for accounting practices (§6) */}
      <section className="max-w-6xl mx-auto px-6 py-20" data-testid="practices-section">
        <div className="max-w-2xl">
          <div className="overline mb-3" style={{ color: "#F37A24" }}>Built for accounting practices</div>
          <h2 className="font-heading font-bold text-navy text-3xl sm:text-4xl tracking-tight">
            Standardise month-end across every client
          </h2>
          <p className="text-navy/70 mt-4">Run one consistent process across all your Xero organisations — and scale without adding month-end headcount.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {PRACTICES.map((p) => (
            <div key={p.t} data-testid={`practice-${p.t}`} className="card-interactive p-6">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(242,111,33,0.1)" }}>
                <p.icon size={22} weight="duotone" style={{ color: "#F37A24" }} />
              </div>
              <h3 className="font-heading font-bold text-navy text-base mb-2">{p.t}</h3>
              <p className="text-navy/70 text-sm leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trust / background (§9) */}
      <section className="bg-appbg border-y border-slate-200" data-testid="built-by-section">
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <div className="overline mb-3" style={{ color: "#F37A24" }}>Built by accountants</div>
          <h2 className="font-heading font-bold text-navy text-3xl sm:text-4xl tracking-tight max-w-2xl mx-auto">
            Made by a practice that lives month-end
          </h2>
          <p className="text-navy/70 mt-5 max-w-2xl mx-auto leading-relaxed">
            Lottie is built by the accountants at <strong className="text-navy">Cloud Accounting Support Services Ltd</strong> —
            a <strong className="text-navy">Xero Platinum Partner</strong>, not a software startup guessing at the problem.
            After years of wrestling accruals, prepayments and deferred income across dozens of Xero clients in fragile
            spreadsheets, we built the tool we always wished existed: one that posts the journals, keeps the registers
            current and never forgets a release. Lottie works the way accountants actually work in Xero — because
            accountants built her.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mt-8 text-navy/70 text-sm font-semibold">
            <span className="flex items-center gap-2"><Certificate size={20} weight="fill" style={{ color: "#F37A24" }} /> Xero Platinum Partner</span>
            <span className="flex items-center gap-2"><Buildings size={20} weight="duotone" style={{ color: "#F37A24" }} /> Cloud Accounting Support Services Ltd</span>
            <span className="flex items-center gap-2"><ShieldCheck size={20} weight="duotone" style={{ color: "#F37A24" }} /> Built inside a real practice</span>
          </div>
        </div>
      </section>

      {/* Security & trust */}
      <section className="max-w-6xl mx-auto px-6 py-20" data-testid="security-section">
        <div className="max-w-2xl">
          <div className="overline mb-3" style={{ color: "#F37A24" }}>Security &amp; trust</div>
          <h2 className="font-heading font-bold text-navy text-3xl sm:text-4xl tracking-tight">
            Connect your Xero data with confidence
          </h2>
          <p className="text-navy/70 mt-4">Secure connection, a full audit trail, and journals that never move without your say-so.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {SECURITY.map((s) => (
            <div key={s.t} data-testid={`security-${s.t}`} className="card-surface p-6">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(242,111,33,0.1)" }}>
                <s.icon size={22} weight="duotone" style={{ color: "#F37A24" }} />
              </div>
              <h3 className="font-heading font-bold text-navy text-base mb-2">{s.t}</h3>
              <p className="text-navy/70 text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
        <p className="text-navy/65 text-xs leading-relaxed mt-8 max-w-3xl" data-testid="data-handling-note">
          WorkWithLottie uses established cloud infrastructure providers to operate and secure the service. Some
          information may be processed outside the United Kingdom. Where international transfers occur, appropriate
          contractual and legal safeguards are used in accordance with UK data-protection law.
        </p>
      </section>

      {/* Testimonial */}
      <section className="bg-appbg border-y border-slate-200" data-testid="testimonial-section">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <Quotes size={40} weight="fill" style={{ color: "#F37A24" }} className="mx-auto mb-6" />
          <blockquote className="font-heading font-bold text-navy text-xl sm:text-2xl leading-snug tracking-tight">
            &ldquo;{TESTIMONIAL.quote}&rdquo;
          </blockquote>
          <div className="mt-6">
            <div className="font-semibold text-navy">{TESTIMONIAL.name}</div>
            <div className="text-navy/70 text-sm">{TESTIMONIAL.role}</div>
          </div>
        </div>
      </section>

      {/* Pricing (§10) */}
      <section className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center" data-testid="pricing-section">
        <div>
          <div className="overline mb-3" style={{ color: "#F37A24" }}>Simple pricing</div>
          <h2 className="font-heading font-bold text-navy text-3xl sm:text-4xl tracking-tight mb-5">
            One plan, priced per Xero organisation
          </h2>
          <ul className="space-y-3">
            {["Every feature included — no tiers to decode", "Add or remove organisations any time", "7-day free trial, no card required", "Cancel whenever you like"].map((t) => (
              <li key={t} className="flex gap-3 text-navy/70">
                <CheckCircle size={20} weight="fill" style={{ color: "#F37A24" }} className="shrink-0 mt-0.5" /> {t}
              </li>
            ))}
          </ul>
          <Link to="/pricing" data-testid="pricing-view-link"
            className="inline-flex items-center gap-2 mt-8 text-navy font-semibold px-6 py-3.5 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors">
            View Pricing <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-8">
          <PriceCalculator />
        </div>
      </section>

      {/* CTA band (§12) */}
      <section className="max-w-6xl mx-auto px-6 pb-4">
        <div className="rounded-3xl px-8 py-14 text-center" style={{ background: "linear-gradient(135deg,#0E0F3F 0%,#13294a 100%)" }}>
          <h2 className="font-heading font-black text-white text-3xl sm:text-4xl tracking-tight">Give month-end back to your team</h2>
          <p className="text-white/60 mt-4 max-w-xl mx-auto">Start free for 7 days. Connect Xero, pick your codes, and let Lottie run the register — with you in control.</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/start" data-testid="cta-band-trial"
              className="inline-flex items-center gap-2 text-white font-semibold px-7 py-3.5 rounded-xl transition-transform hover:scale-[1.02]"
              style={{ background: "#F37A24" }}>
              Start Free Trial <ArrowRight size={18} weight="bold" />
            </Link>
            <Link to="/pricing" data-testid="cta-band-pricing"
              className="inline-flex items-center gap-2 text-white font-semibold px-7 py-3.5 rounded-xl border border-white/20 hover:border-white/40 transition-colors">
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
